/**
 * 個別説明記録 入力画面 (新規記録 + 既存記録閲覧両対応)。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §7.6
 */
import type {
  ConsiderationGroup,
  ConsideredProductItem,
  ConversionLevel,
  Installments,
  LineDiscountType,
  PaymentMethod,
  TradeInType,
} from '@goenhub/shared';
import { calcLineAmounts } from '@goenhub/shared';
import {
  ArrowLeft,
  CalendarCheck,
  CalendarX,
  ClipboardList,
  Cpu,
  ExternalLink,
  NotebookPen,
  FileText,
  Plus,
  Save,
  Tag,
  Trash2,
  Undo2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { BriefingApplicationDetails } from './BriefingApplicationDetails';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useBriefingContacts,
  useBriefingRecords,
  useBriefingSchedules,
  useMarkBriefingNotConducted,
  useSaveBriefingRecord,
  useUndoBriefingNotConducted,
} from '@/hooks/useBriefings';
import { useContactTimeline, type ContactTimelineEvent } from '@/hooks/useContactTimeline';
import { useDeal } from '@/hooks/useDeals';
import { useAllActiveCatalogItems } from '@/hooks/useProductCatalogs';
import { useSoldDevices } from '@/hooks/useSalesOps';
import { paths } from '@/routes/paths';

interface FormState {
  conversionLevel: ConversionLevel;
  inquiries: string;
  introductionImage: string;
  concerns: string;
  consideredProducts: ConsiderationGroup[];
  purchaseTimeline: string;
  paymentMethod: PaymentMethod;
  installments: Installments | null;
  hasTradeIn: boolean;
  tradeInType: TradeInType | null;
  saleAware: boolean;
  saleName: string;
  hasIncentive: boolean;
  incentiveName: string;
  nextAction: string;
  supplementaryNotes: string;
}

const defaults: FormState = {
  conversionLevel: 3,
  inquiries: '',
  introductionImage: '',
  concerns: '',
  consideredProducts: [],
  purchaseTimeline: '',
  paymentMethod: 'undecided',
  installments: null,
  hasTradeIn: false,
  tradeInType: null,
  saleAware: false,
  saleName: '',
  hasIncentive: false,
  incentiveName: '',
  nextAction: '',
  supplementaryNotes: '',
};

function tsToDate(v: unknown): Date | null {
  if (!v) return null;
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate();
  }
  return null;
}

// ─── 行動履歴 (audience_events) の表示ラベル ───
const TIMELINE_EVENT_LABEL: Record<string, string> = {
  mail_sent: 'メール送信',
  mail_open: 'メール開封',
  line_news_received: 'LINE 受信',
  form_submitted: 'フォーム送信',
  landing_view: 'ランディング閲覧',
};

const TIMELINE_TARGET_PREFIX: Record<string, string> = {
  mail_schedule: 'メール',
  line_news: 'ニュース',
  form: 'フォーム',
  landing: 'LP',
  document: '資料',
};

function formatTimelineTs(ts: ContactTimelineEvent['timestamp']): string {
  if (!ts) return '';
  try {
    return ts.toDate().toLocaleString('ja-JP', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function BriefingRecordPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  // 戻り先: 一覧から来た場合はその URL (タブ・絞り込み込み、2026-10-07)。直接開いた場合は個別説明画面
  const location = useLocation();
  const navState = location.state as { from?: string; rowKey?: string } | null;
  const backTo = navState?.from?.startsWith('/') ? navState.from : '/briefings';
  const goBack = () =>
    navigate(backTo, { state: navState?.rowKey ? { focusRowKey: navState.rowKey } : null });
  const { data: deal, isLoading: dealLoading } = useDeal(dealId);
  // 顧客名の解決用。外部SA 以外は全連絡先 (limit を付けると枠外の連絡先名が解決できず
  // 顧客名未確定になる)。外部SA は自分の顧客 + 自分に関係する申込の顧客 (2026-10-16)。
  const contacts = useBriefingContacts(useMemo(() => (deal ? [deal] : undefined), [deal]));
  const { data: records, isLoading: recordsLoading } = useBriefingRecords(dealId);
  const schedules = useBriefingSchedules();
  const saveMutation = useSaveBriefingRecord();
  const markNotConducted = useMarkBriefingNotConducted();
  const undoNotConducted = useUndoBriefingNotConducted();
  const { user } = useAuth();
  // 過去の行動履歴 (audience_events)。deal 未ロード時は無効化
  const timeline = useContactTimeline({
    contactId: deal?.contactId ?? null,
    limit: 20,
    enabled: !!deal?.contactId,
  });
  // 所有商品 (sold_devices)。deal 未ロード時は該当なしIDで空クエリ (全件取得を防ぐ)
  const soldDevices = useSoldDevices({
    contactId: deal?.contactId ?? '__none__',
    includeAsOwner: true,
  });
  // 検討商品の商品マスタ。割引バリデーション (親) と製品プルダウン (子) で共用する
  const { data: catalogItems } = useAllActiveCatalogItems();
  const products = useMemo<ConsideredProductOption[]>(
    () =>
      (catalogItems ?? []).map((it) => ({
        id: it.id,
        name: it.name,
        modelName: it.modelName,
        priceExcludingTax: it.priceExcludingTax,
        priceIncludingTax: it.priceIncludingTax,
      })),
    [catalogItems],
  );
  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const [state, setState] = useState<FormState>(defaults);
  const [notConductedDialogOpen, setNotConductedDialogOpen] = useState(false);
  const [notConductedNote, setNotConductedNote] = useState('');
  // 未実施マーク (not_conducted) は編集対象の記録としては扱わない
  const existingRecord =
    records?.find((r) => (r.status ?? 'completed') !== 'not_conducted') ?? null;
  const notConductedRecord =
    records?.find((r) => (r.status ?? 'completed') === 'not_conducted') ?? null;

  useEffect(() => {
    if (existingRecord) {
      setState({
        conversionLevel: existingRecord.conversionLevel,
        inquiries: existingRecord.inquiries ?? '',
        introductionImage: existingRecord.introductionImage ?? '',
        concerns: existingRecord.concerns ?? '',
        consideredProducts: existingRecord.consideredProducts ?? [],
        purchaseTimeline: existingRecord.purchaseTimeline ?? '',
        paymentMethod: existingRecord.paymentMethod,
        installments: existingRecord.installments ?? null,
        hasTradeIn: existingRecord.hasTradeIn,
        tradeInType: existingRecord.tradeInType ?? null,
        saleAware: existingRecord.saleAware,
        saleName: existingRecord.saleName ?? '',
        hasIncentive: existingRecord.hasIncentive,
        incentiveName: existingRecord.incentiveName ?? '',
        nextAction: existingRecord.nextAction ?? '',
        supplementaryNotes: existingRecord.supplementaryNotes ?? '',
      });
    }
  }, [existingRecord]);

  /**
   * 値引き額が行小計を超えている検討商品を洗い出す (2026-08-22)。
   * 「値引き額」欄に商品価格を入力してしまうと行が 0 円になり、
   * そのまま見積書を作ると全額 0 円の見積が出てしまうため保存時に止める。
   *
   * 注意: フックはローディング時の早期 return より前に置くこと。
   * 早期 return の後に置くとレンダー間でフック数が変わり React #310 で
   * 画面全体がクラッシュする (2026-08-24 実害: 記録/詳細ボタンで真っ白)。
   */
  const overflowItems = useMemo(() => {
    const out: string[] = [];
    state.consideredProducts.forEach((g, gi) => {
      const label = g.label?.trim() || `検討${String(gi + 1).padStart(2, '0')}`;
      (g.items ?? []).forEach((it) => {
        const p = it.productId ? productById.get(it.productId) : undefined;
        if (!p) return;
        const amounts = calcLineAmounts({
          priceExcludingTax: p.priceExcludingTax,
          priceIncludingTax: p.priceIncludingTax,
          quantity: it.quantity,
          discountType: it.discountType,
          discountValue: it.discountValue,
        });
        if (amounts.discountClamped) out.push(`${label} / ${it.productName}`);
      });
    });
    return out;
  }, [state.consideredProducts, productById]);

  if (dealLoading || recordsLoading || !deal) {
    return (
      <Screen title="個別説明記録">
        {dealLoading || recordsLoading ? (
          <Spinner />
        ) : (
          <EmptyState
            icon={FileText}
            title="商談が見つかりません"
            description="削除されたか、URL が誤っている可能性があります。"
          />
        )}
      </Screen>
    );
  }

  const contact = contacts?.find((c) => c.id === deal.contactId);
  const contactKana = [contact?.lastNameKana, contact?.firstNameKana].filter(Boolean).join(' ');
  const scheduledAt = tsToDate(deal.scheduledBriefingAt);
  // 保存済み記録は実施担当者 (conductedBy) 本人のみ編集可能 (2026-07-23 仕様更新)
  const isConductor = !!existingRecord && !!user?.uid && existingRecord.conductedBy === user.uid;
  // 未実施マーク済みの deal は記録入力不可 (取消してから入力)
  const isReadonly = (!!existingRecord && !isConductor) || !!notConductedRecord;
  const isDraft = !!existingRecord && (existingRecord.status ?? 'completed') === 'draft';
  const isManager = user?.isOwner || user?.role === 'owner' || user?.role === 'editor';
  const canUndoNotConducted =
    !!notConductedRecord && (notConductedRecord.conductedBy === user?.uid || isManager);

  const handleMarkNotConducted = async () => {
    if (!dealId) return;
    try {
      await markNotConducted.mutateAsync({
        dealId,
        note: notConductedNote.trim() || null,
      });
      toast.success('未実施としてマークしました (代行料の請求対象外になります)');
      setNotConductedDialogOpen(false);
      setNotConductedNote('');
      goBack();
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleUndoNotConducted = async () => {
    if (!dealId || !notConductedRecord) return;
    if (!confirm('未実施マークを取り消して、確定状態 (記録待ち) に戻します。よろしいですか？'))
      return;
    try {
      await undoNotConducted.mutateAsync({
        dealId,
        recordId: notConductedRecord.id,
      });
      toast.success('未実施マークを取り消しました');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleSave = async (saveStatus: 'draft' | 'completed') => {
    if (!dealId || isReadonly) return;
    if (overflowItems.length > 0) {
      toast.error(
        `値引き額が商品の小計を超えています (${overflowItems.join(' / ')})。値引き欄には「商品価格」ではなく「値引き分の金額」を入力してください。`,
      );
      return;
    }
    // 更新モード (既存記録の編集) では日程チェック不要 (手動追加記録は日程なしがあり得る)
    if (!existingRecord && !deal.scheduledBriefingAt) {
      toast.error('個別説明日程が確定していません。先に日程確定を行ってください。');
      return;
    }
    // briefingScheduleId を deal.activities から復元するのは Phase 5 の MVP では未実装。
    // 代わりに、Deal の scheduledBriefingAt 確定時の payload からは取れないため、
    // 直近の同一 assignee + scheduledAt 一致の briefing_schedules を逆引きせず、
    // scheduleId 引数として deal の確定時に保存した値を使う運用を Phase 6 で整備する。
    // ここでは仮に dealId をスケジュール参照として使い、saveBriefingRecord 側で受領する。
    // (MVP: briefingScheduleId は活用しない、Phase 6 で正規化)
    try {
      await saveMutation.mutateAsync({
        dealId,
        status: saveStatus,
        ...(existingRecord ? { recordId: existingRecord.id } : {}),
        briefingScheduleId: existingRecord
          ? (existingRecord.briefingScheduleId ?? null)
          : 'unknown', // Phase 6 で正規化
        conversionLevel: state.conversionLevel,
        interestedProductIds: [],
        consideredProducts: state.consideredProducts,
        inquiries: state.inquiries,
        introductionImage: state.introductionImage,
        concerns: state.concerns,
        purchaseTimeline: state.purchaseTimeline,
        paymentMethod: state.paymentMethod,
        installments: state.installments,
        hasTradeIn: state.hasTradeIn,
        tradeInType: state.tradeInType,
        saleAware: state.saleAware,
        saleName: state.saleName.trim() || null,
        hasIncentive: state.hasIncentive,
        incentiveName: state.incentiveName.trim() || null,
        nextAction: state.nextAction,
        supplementaryNotes: state.supplementaryNotes,
      });
      toast.success(
        saveStatus === 'draft'
          ? '下書きを保存しました'
          : existingRecord
            ? '記録を更新しました'
            : '記録を保存しました',
      );
      if (saveStatus === 'completed') goBack();
    } catch (err) {
      toast.error(`保存失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setState((s) => ({ ...s, [key]: value }));
  };

  return (
    <Screen
      title={`個別説明記録 — ${contact?.name ?? '(顧客名未確定)'}`}
      description={deal.title}
      actions={
        <div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={goBack}>
            <ArrowLeft />
            戻る
          </Button>
          {notConductedRecord && canUndoNotConducted && (
            <Button
              type="button"
              variant="secondary"
              onClick={handleUndoNotConducted}
              disabled={undoNotConducted.isPending}
              title="未実施マークを取り消して確定状態 (記録待ち) に戻します"
            >
              <Undo2 />
              {undoNotConducted.isPending ? '取消中...' : '未実施を取消'}
            </Button>
          )}
          {!notConductedRecord && !existingRecord && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setNotConductedDialogOpen(true)}
              disabled={markNotConducted.isPending}
              title="個別説明が実施されなかった場合に未実施としてクローズします (代行料の請求対象外)"
            >
              <CalendarX />
              未実施
            </Button>
          )}
          {!isReadonly && (!existingRecord || isDraft) && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => handleSave('draft')}
              disabled={saveMutation.isPending}
              title="完了扱いにせず途中保存します (後から編集できます)"
            >
              <Save />
              {saveMutation.isPending ? '保存中...' : '下書き保存'}
            </Button>
          )}
          {!isReadonly && (
            <Button
              type="button"
              variant="primary"
              onClick={() => handleSave('completed')}
              disabled={saveMutation.isPending}
            >
              <Save />
              {saveMutation.isPending
                ? '保存中...'
                : existingRecord && !isDraft
                  ? '記録を更新'
                  : '個別説明終了'}
            </Button>
          )}
        </div>
      }
    >
      {/* 未実施バナー */}
      {notConductedRecord && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-[var(--accent-amber,_#d4a373)] bg-[var(--bg-elevated)] px-4 py-3">
          <CalendarX className="size-4 shrink-0 mt-0.5 text-[var(--accent-amber,_#d4a373)]" />
          <div className="text-xs space-y-0.5">
            <div className="font-medium">この個別説明は「未実施」としてマークされています</div>
            <div className="text-[var(--text-muted)]">
              マーク日時: {tsToDate(notConductedRecord.conductedAt)?.toLocaleString('ja-JP') ?? '—'}
              {notConductedRecord.supplementaryNotes
                ? ` ／ 理由: ${notConductedRecord.supplementaryNotes}`
                : ''}
            </div>
            <div className="text-[var(--text-muted)]">
              個別説明代行料の請求対象外です。実際には実施していた場合は「未実施を取消」してから記録を保存してください。
            </div>
          </div>
        </div>
      )}

      {/* 未実施マーク 確認ダイアログ */}
      <Dialog open={notConductedDialogOpen} onOpenChange={setNotConductedDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>個別説明を未実施としてマーク</DialogTitle>
            <DialogDescription>
              この確定済み個別説明を「未実施」としてクローズします。完了タブに「未実施」として表示され、
              SA
              の個別説明代行料の請求対象にはなりません。誤ってマークした場合は後から取消できます。
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>理由 (任意)</Label>
            <Textarea
              value={notConductedNote}
              onChange={(e) => setNotConductedNote(e.target.value)}
              placeholder="例: 顧客都合によるキャンセル / 無断キャンセル / 日程再調整中"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-[var(--border)]">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setNotConductedDialogOpen(false)}
              disabled={markNotConducted.isPending}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleMarkNotConducted}
              disabled={markNotConducted.isPending}
            >
              <CalendarX />
              {markNotConducted.isPending ? 'マーク中...' : '未実施としてマーク'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 左: 顧客・申込情報 (読み取り専用) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-4">
          {/* 顧客情報 */}
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
                顧客情報
              </span>
              <Link
                to={paths.salesContactDetail.replace(':contactId', deal.contactId)}
                className="inline-flex items-center gap-1 text-[11px] text-[var(--accent)] hover:underline"
                title="顧客詳細ページを開く"
              >
                顧客詳細
                <ExternalLink className="size-3" />
              </Link>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-semibold text-[var(--text-primary)]">
                {contact?.name ?? '—'}
              </span>
              <Badge variant={contact?.kind === 'btob' ? 'accent' : 'review'}>
                {contact?.kind === 'btob' ? 'BtoB' : contact?.kind === 'btoc' ? 'BtoC' : 'Unknown'}
              </Badge>
            </div>
            {contactKana && (
              <div className="text-[11px] text-[var(--text-muted)] -mt-1">{contactKana}</div>
            )}
            <div className="text-xs text-[var(--text-secondary)] space-y-0.5">
              <div>{contact?.defaultEmail ?? '—'}</div>
              {contact?.phone && <div>{contact.phone}</div>}
            </div>
            {(contact?.tags ?? []).length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1">
                {(contact?.tags ?? []).map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] bg-[var(--bg-elevated)] px-2 py-0.5 text-[10px] text-[var(--text-secondary)]"
                  >
                    <Tag className="size-2.5" />
                    {t}
                  </span>
                ))}
              </div>
            )}
            {/* 背景記録 (salesMemo): 決裁者・予算・歴史的経緯など商談を跨ぐコンテキスト */}
            {contact?.salesMemo?.trim() && (
              <div className="pt-2 mt-1 border-t border-[var(--border)] space-y-1">
                <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider flex items-center gap-1.5">
                  <NotebookPen className="size-3" />
                  背景記録
                </div>
                <p className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
                  {contact.salesMemo}
                </p>
              </div>
            )}
          </div>

          {/* 申込情報 */}
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 space-y-2">
            <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
              申込情報
            </div>
            <div className="text-xs">主軸カテゴリ: {deal.primaryCategory ?? '—'}</div>
            <div className="text-xs flex items-center gap-1.5">
              <CalendarCheck className="size-3.5 text-[var(--accent-green)] flex-shrink-0" />
              <span>
                日程:{' '}
                {scheduledAt
                  ? `${scheduledAt.getFullYear()}/${scheduledAt.getMonth() + 1}/${scheduledAt.getDate()} ${String(scheduledAt.getHours()).padStart(2, '0')}:${String(scheduledAt.getMinutes()).padStart(2, '0')}`
                  : '—'}
                {deal.briefingParticipantCount ? ` ・ 参加${deal.briefingParticipantCount}名` : ''}
              </span>
            </div>
            {existingRecord && (
              <div className="mt-2 pt-2 border-t border-[var(--border)] text-xs text-[var(--text-muted)] space-y-0.5">
                <div>
                  {isDraft ? '✎ 下書き保存中' : '✓ 記録済み'} (
                  {tsToDate(existingRecord.conductedAt)?.toLocaleString('ja-JP') ?? '—'})
                </div>
                {tsToDate(existingRecord.updatedAt) && (
                  <div>最終編集: {tsToDate(existingRecord.updatedAt)?.toLocaleString('ja-JP')}</div>
                )}
                <div>
                  {isConductor
                    ? 'あなたが実施担当者のため編集できます。'
                    : '編集は実施担当者本人のみ可能です。'}
                </div>
              </div>
            )}
          </div>

          {/* 所有商品 (sold_devices) */}
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 space-y-2">
            <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider flex items-center gap-1.5">
              <Cpu className="size-3" />
              所有商品
              {(soldDevices.data?.length ?? 0) > 0 && (
                <span className="normal-case tracking-normal">({soldDevices.data?.length})</span>
              )}
            </div>
            {soldDevices.isLoading ? (
              <Spinner />
            ) : soldDevices.isError ? (
              <p className="text-xs text-[var(--text-muted)]">
                所有商品を取得できませんでした (閲覧権限がない可能性があります)
              </p>
            ) : !soldDevices.data || soldDevices.data.length === 0 ? (
              <p className="text-xs text-[var(--text-muted)]">
                所有商品はありません (納品完了で自動登録されます)
              </p>
            ) : (
              <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                {soldDevices.data.map((d) => (
                  <div
                    key={d.id}
                    className={`flex items-start gap-2.5 px-2.5 py-1.5 rounded border border-[var(--border)] bg-[var(--bg-elevated)] ${
                      d.decommissioned ? 'opacity-50' : ''
                    }`}
                    title={d.decommissioned ? 'アーカイブ済み機器' : undefined}
                  >
                    <Cpu className="size-3.5 text-[var(--accent-cyan,_#00bbf9)] flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-medium text-[var(--text-primary)] truncate">
                        {d.productName}
                        {d.decommissioned ? ' (アーカイブ済)' : ''}
                      </div>
                      <div className="text-[10px] text-[var(--text-muted)] truncate">
                        {d.modelName}
                        {d.variation ? ` / ${d.variation}` : ''}
                        {d.serialNumber ? ` ・ SN: ${d.serialNumber}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 申込フォームの回答 */}
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] overflow-hidden">
            <div className="px-4 pt-3 pb-2 text-[10px] text-[var(--text-muted)] uppercase tracking-wider flex items-center gap-1.5">
              <FileText className="size-3" />
              申込フォームの回答
            </div>
            <BriefingApplicationDetails deal={deal} schedules={schedules.data ?? []} />
          </div>

          {/* 過去の行動履歴 */}
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 space-y-2">
            <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider flex items-center gap-1.5">
              <ClipboardList className="size-3" />
              過去の行動履歴
            </div>
            {timeline.isLoading ? (
              <Spinner />
            ) : !timeline.data || timeline.data.length === 0 ? (
              <p className="text-xs text-[var(--text-muted)]">
                行動履歴がありません (メール開封・フォーム送信などが蓄積されると表示されます)
              </p>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                {timeline.data.map((ev) => (
                  <div
                    key={ev.id}
                    className="flex items-start gap-2.5 px-2.5 py-1.5 rounded border border-[var(--border)] bg-[var(--bg-elevated)]"
                  >
                    <div className="text-[10px] text-[var(--text-muted)] tabular-nums whitespace-nowrap pt-0.5">
                      {formatTimelineTs(ev.timestamp)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-medium text-[var(--text-primary)]">
                        {TIMELINE_EVENT_LABEL[ev.type] ?? ev.type}
                      </div>
                      {ev.targetLabel && (
                        <div className="text-[10px] text-[var(--text-muted)] truncate">
                          {TIMELINE_TARGET_PREFIX[ev.targetType] ?? ''}: {ev.targetLabel}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 右: 記録入力 */}
        <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 space-y-3">
          <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">
            記録項目
          </div>
          <div>
            <Label>成約レベル (1〜5)</Label>
            <Select
              value={String(state.conversionLevel)}
              onValueChange={(v) => update('conversionLevel', Number(v) as ConversionLevel)}
              disabled={isReadonly}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>知りたいこと/気になっていること</Label>
            <Textarea
              rows={2}
              value={state.inquiries}
              onChange={(e) => update('inquiries', e.target.value)}
              disabled={isReadonly}
            />
          </div>
          <div>
            <Label>導入イメージ</Label>
            <Textarea
              rows={2}
              value={state.introductionImage}
              onChange={(e) => update('introductionImage', e.target.value)}
              disabled={isReadonly}
            />
          </div>
          <div>
            <Label>導入への不安や悩み</Label>
            <Textarea
              rows={2}
              value={state.concerns}
              onChange={(e) => update('concerns', e.target.value)}
              disabled={isReadonly}
            />
          </div>

          <ConsideredProductsSection
            value={state.consideredProducts}
            onChange={(v) => update('consideredProducts', v)}
            products={products}
            disabled={isReadonly}
          />

          <div>
            <Label>購入予定時期</Label>
            <Input
              value={state.purchaseTimeline}
              onChange={(e) => update('purchaseTimeline', e.target.value)}
              disabled={isReadonly}
              placeholder="例: 来月 / 半年以内 / 検討中"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>支払い方法</Label>
              <Select
                value={state.paymentMethod}
                onValueChange={(v) => update('paymentMethod', v as PaymentMethod)}
                disabled={isReadonly}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="undecided">未定</SelectItem>
                  <SelectItem value="transfer">振込</SelectItem>
                  <SelectItem value="credit_card">クレカ</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>分割回数</Label>
              <Select
                value={state.installments != null ? String(state.installments) : '__none__'}
                onValueChange={(v) =>
                  update('installments', v === '__none__' ? null : (Number(v) as Installments))
                }
                disabled={isReadonly}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">未設定</SelectItem>
                  {[12, 24, 36, 48, 60].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}回
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="rounded-sm border border-[var(--border)] px-3 py-2 flex items-center justify-between">
            <Label className="!mb-0">下取り検討</Label>
            <Switch
              checked={state.hasTradeIn}
              onChange={(e) => update('hasTradeIn', e.target.checked)}
              disabled={isReadonly}
            />
          </div>
          {state.hasTradeIn && (
            <div>
              <Label>下取りタイプ</Label>
              <Select
                value={state.tradeInType ?? '__none__'}
                onValueChange={(v) =>
                  update('tradeInType', v === '__none__' ? null : (v as TradeInType))
                }
                disabled={isReadonly}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">未指定</SelectItem>
                  <SelectItem value="discount">割引</SelectItem>
                  <SelectItem value="cashback">キャッシュバック</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-sm border border-[var(--border)] px-3 py-2 flex items-center justify-between">
              <Label className="!mb-0">セール意識あり</Label>
              <Switch
                checked={state.saleAware}
                onChange={(e) => update('saleAware', e.target.checked)}
                disabled={isReadonly}
              />
            </div>
            <div className="rounded-sm border border-[var(--border)] px-3 py-2 flex items-center justify-between">
              <Label className="!mb-0">特典希望</Label>
              <Switch
                checked={state.hasIncentive}
                onChange={(e) => update('hasIncentive', e.target.checked)}
                disabled={isReadonly}
              />
            </div>
          </div>
          {state.saleAware && (
            <div>
              <Label>セール名</Label>
              <Input
                value={state.saleName}
                onChange={(e) => update('saleName', e.target.value)}
                disabled={isReadonly}
              />
            </div>
          )}
          {state.hasIncentive && (
            <div>
              <Label>特典名</Label>
              <Input
                value={state.incentiveName}
                onChange={(e) => update('incentiveName', e.target.value)}
                disabled={isReadonly}
              />
            </div>
          )}
          <div>
            <Label>次のアクション</Label>
            <Textarea
              rows={2}
              value={state.nextAction}
              onChange={(e) => update('nextAction', e.target.value)}
              disabled={isReadonly}
              placeholder="例: 来週見積送付、再アプローチ"
            />
          </div>
          <div>
            <Label>補足情報</Label>
            <Textarea
              rows={2}
              value={state.supplementaryNotes}
              onChange={(e) => update('supplementaryNotes', e.target.value)}
              disabled={isReadonly}
            />
          </div>
        </div>
      </div>
    </Screen>
  );
}

// ────────────────────────────────────────────────
// 検討商品セクション
// ────────────────────────────────────────────────

const FREE_TEXT_VALUE = '__free__';

/** 検討商品プルダウンの選択肢 (価格は値引きプレビュー / 検証に使う)。 */
export interface ConsideredProductOption {
  id: string;
  name: string;
  modelName?: string;
  priceExcludingTax: number;
  priceIncludingTax: number;
}

interface ConsideredProductsSectionProps {
  value: ConsiderationGroup[];
  onChange: (v: ConsiderationGroup[]) => void;
  /** 商品マスタ (親が useAllActiveCatalogItems から供給) */
  products: ConsideredProductOption[];
  disabled?: boolean;
}

function ConsideredProductsSection({
  value,
  onChange,
  products,
  disabled,
}: ConsideredProductsSectionProps) {
  const addGroup = () => {
    onChange([...value, { label: null, items: [emptyItem()] }]);
  };

  const updateGroup = (idx: number, next: ConsiderationGroup) => {
    onChange(value.map((g, i) => (i === idx ? next : g)));
  };

  const removeGroup = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <div className="rounded-sm border border-[var(--border)] p-3 space-y-3">
      <div className="flex items-center justify-between">
        <Label className="!mb-0">検討商品</Label>
        {!disabled && (
          <Button type="button" variant="ghost" onClick={addGroup}>
            <Plus className="size-3.5" />
            検討を追加
          </Button>
        )}
      </div>
      {value.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)]">
          検討された商品の組み合わせを追加してください
        </p>
      ) : (
        value.map((g, gi) => (
          <ConsiderationGroupCard
            key={gi}
            index={gi}
            group={g}
            products={products ?? []}
            disabled={disabled}
            onChange={(next) => updateGroup(gi, next)}
            onRemove={() => removeGroup(gi)}
          />
        ))
      )}
    </div>
  );
}

function emptyItem(): ConsideredProductItem {
  return { productId: null, productName: '', quantity: 1 };
}

interface ConsiderationGroupCardProps {
  index: number;
  group: ConsiderationGroup;
  products: ConsideredProductOption[];
  disabled?: boolean;
  onChange: (g: ConsiderationGroup) => void;
  onRemove: () => void;
}

function ConsiderationGroupCard({
  index,
  group,
  products,
  disabled,
  onChange,
  onRemove,
}: ConsiderationGroupCardProps) {
  const updateItem = (itemIdx: number, next: ConsideredProductItem) => {
    onChange({
      ...group,
      items: group.items.map((it, i) => (i === itemIdx ? next : it)),
    });
  };

  const addItem = () => {
    onChange({ ...group, items: [...group.items, emptyItem()] });
  };

  const removeItem = (itemIdx: number) => {
    onChange({
      ...group,
      items: group.items.filter((_, i) => i !== itemIdx),
    });
  };

  return (
    <div className="rounded-sm border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-[var(--text-muted)] font-mono shrink-0">
          検討{String(index + 1).padStart(2, '0')}
        </span>
        <Input
          value={group.label ?? ''}
          onChange={(e) => onChange({ ...group, label: e.target.value || null })}
          placeholder="ラベル（任意）例: メイン案 / 予算重視案"
          disabled={disabled}
          className="flex-1"
        />
        {!disabled && (
          <Button type="button" variant="ghost" onClick={onRemove} aria-label="検討を削除">
            <Trash2 className="size-3.5" />
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {group.items.map((it, ii) => (
          <ConsideredItemRow
            key={ii}
            item={it}
            products={products}
            disabled={disabled}
            onChange={(next) => updateItem(ii, next)}
            onRemove={() => removeItem(ii)}
            canRemove={group.items.length > 1}
          />
        ))}
      </div>

      {!disabled && (
        <Button type="button" variant="ghost" onClick={addItem}>
          <Plus className="size-3.5" />
          製品を追加
        </Button>
      )}
    </div>
  );
}

interface ConsideredItemRowProps {
  item: ConsideredProductItem;
  products: ConsideredProductOption[];
  disabled?: boolean;
  onChange: (it: ConsideredProductItem) => void;
  onRemove: () => void;
  canRemove: boolean;
}

function ConsideredItemRow({
  item,
  products,
  disabled,
  onChange,
  onRemove,
  canRemove,
}: ConsideredItemRowProps) {
  const isFree = item.productId === null;
  const selectValue = isFree ? FREE_TEXT_VALUE : (item.productId ?? FREE_TEXT_VALUE);
  // 選択中の商品 (自由記述行は価格不明 = プレビューなし)
  const selected = item.productId ? products.find((p) => p.id === item.productId) : undefined;
  const amounts = selected
    ? calcLineAmounts({
        priceExcludingTax: selected.priceExcludingTax,
        priceIncludingTax: selected.priceIncludingTax,
        quantity: item.quantity,
        discountType: item.discountType,
        discountValue: item.discountValue,
      })
    : null;
  const hasDiscount = (amounts?.discountAmountExcludingTax ?? 0) > 0;
  const isOverflow = amounts?.discountClamped ?? false;

  const handleSelect = (v: string) => {
    if (v === FREE_TEXT_VALUE) {
      onChange({ ...item, productId: null, productName: '' });
      return;
    }
    const p = products.find((x) => x.id === v);
    onChange({
      ...item,
      productId: v,
      productName: p?.name ?? '',
    });
  };

  return (
    <div className="space-y-1.5">
      <div className="grid grid-cols-[1fr_80px_auto] gap-2 items-start">
        <div className="space-y-1">
          <Select value={selectValue} onValueChange={handleSelect} disabled={disabled}>
            <SelectTrigger>
              <SelectValue placeholder="製品を選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FREE_TEXT_VALUE}>自由記述</SelectItem>
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                  {p.modelName ? ` (${p.modelName})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isFree && (
            <Input
              value={item.productName}
              onChange={(e) => onChange({ ...item, productName: e.target.value })}
              placeholder="製品名（自由記述）"
              disabled={disabled}
            />
          )}
        </div>
        <Input
          type="number"
          min={1}
          value={item.quantity}
          onChange={(e) =>
            onChange({
              ...item,
              quantity: Math.max(1, Number(e.target.value) || 1),
            })
          }
          disabled={disabled}
        />
        {!disabled && canRemove ? (
          <Button type="button" variant="ghost" onClick={onRemove} aria-label="製品を削除">
            <Trash2 className="size-3.5" />
          </Button>
        ) : (
          <span className="w-9" />
        )}
      </div>
      {/* 値引き額 (税抜に適用)。見積作成へ引き継がれる */}
      <div className="flex flex-wrap items-center gap-2 justify-end">
        {amounts && (
          <span className="mr-auto text-[11px] text-[var(--text-muted)] tabular-nums">
            単価 ¥{selected!.priceIncludingTax.toLocaleString('ja-JP')} (税込)
            {item.quantity > 1 ? ` × ${item.quantity}` : ''} ／ 小計 ¥
            {amounts.baseIncludingTax.toLocaleString('ja-JP')}
          </span>
        )}
        <span className="text-[11px] text-[var(--text-muted)] shrink-0">値引き額 (税抜に適用)</span>
        <Select
          value={item.discountType ?? 'amount'}
          onValueChange={(v) => onChange({ ...item, discountType: v as LineDiscountType })}
          disabled={disabled}
        >
          <SelectTrigger className="w-16">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="amount">¥</SelectItem>
            <SelectItem value="percent">%</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="number"
          min={0}
          value={item.discountValue ?? ''}
          onChange={(e) => {
            const n = Number(e.target.value);
            onChange({
              ...item,
              discountType: item.discountType ?? 'amount',
              discountValue: e.target.value === '' || !Number.isFinite(n) || n <= 0 ? null : n,
            });
          }}
          placeholder={(item.discountType ?? 'amount') === 'percent' ? '例: 10' : '例: 50000'}
          disabled={disabled}
          aria-invalid={isOverflow}
          className={`w-28 ${isOverflow ? 'border-[var(--accent-red)]' : ''}`}
        />
      </div>

      {/* 値引きの結果 / 誤入力の警告。
          「商品価格」を値引き欄に入れてしまう誤操作 (2026-08-21 実害) の再発防止。 */}
      {isOverflow ? (
        <p className="text-right text-[11px] text-[var(--accent-red)]">
          値引き額が小計を超えています。この欄は<strong>商品価格ではなく「値引き分の金額」</strong>
          を入力してください (このままでは行が 0 円になります)
        </p>
      ) : hasDiscount && amounts ? (
        <p className="text-right text-[11px] text-[var(--text-muted)] tabular-nums">
          −¥{amounts.discountAmountExcludingTax.toLocaleString('ja-JP')} (税抜) → 値引き後 小計 ¥
          {amounts.subtotalIncludingTax.toLocaleString('ja-JP')} (税込)
        </p>
      ) : (
        <p className="text-right text-[11px] text-[var(--text-muted)]">
          商品価格ではなく、値引き分の金額 (¥) または割引率 (%)
          を入力してください。値引きなしは空欄のまま
        </p>
      )}
    </div>
  );
}
